/**
 * useHomeGroupAdmin Hooks
 *
 * URQL hooks for admin home group management (query + mutations).
 */

import { useCallback } from "react";
import { useQuery, useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";
import type { AdminAccountRef, HomeGroupScheduleItem, DayOfWeek } from "../types";

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const HomeGroupDocument = `
  query HomeGroup($id: ID!) {
    homeGroup(id: $id) {
      id name description location address mapsUrl
      latitude longitude maxCapacity genderRestriction minAge maxAge
      isActive isOpen imageUrl
      leader { id firstName lastName username }
      coLeader { id firstName lastName username }
      schedules { id dayOfWeek startTime endTime isActive }
      memberCount
      members { id account { id firstName lastName username } role isActive }
    }
  }
`;

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const CreateHomeGroupDocument = `
  mutation CreateHomeGroup($input: HomeGroupInput!) {
    createHomeGroup(input: $input) { id }
  }
`;

const UpdateHomeGroupDocument = `
  mutation UpdateHomeGroup($id: ID!, $input: HomeGroupInput!) {
    updateHomeGroup(id: $id, input: $input) { id }
  }
`;

const DeleteHomeGroupDocument = `
  mutation DeleteHomeGroup($id: ID!) {
    deleteHomeGroup(id: $id)
  }
`;

const AddScheduleDocument = `
  mutation AddHomeGroupSchedule($homeGroupId: ID!, $input: HomeGroupScheduleInput!) {
    addHomeGroupSchedule(homeGroupId: $homeGroupId, input: $input) { id }
  }
`;

const DeleteScheduleDocument = `
  mutation DeleteHomeGroupSchedule($scheduleId: ID!) {
    deleteHomeGroupSchedule(scheduleId: $scheduleId)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

type GenderInput = "MALE" | "FEMALE" | "ANY";

interface HomeGroup {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  address: string | null;
  mapsUrl: string | null;
  maxCapacity: number | null;
  genderRestriction: string;
  minAge: number | null;
  maxAge: number | null;
  isActive: boolean;
  isOpen: boolean;
  imageUrl: string | null;
  leader: AdminAccountRef;
  coLeader: AdminAccountRef | null;
  schedules: HomeGroupScheduleItem[];
  memberCount?: number;
  members: {
    id: string;
    account: AdminAccountRef;
    role: string;
    isActive: boolean;
  }[];
}

interface HomeGroupInput {
  name: string;
  description: string | null;
  leaderId: string;
  coLeaderId: string | null;
  location: string | null;
  address: string | null;
  mapsUrl: string | null;
  maxCapacity: number | null;
  genderRestriction: GenderInput;
  minAge: number | null;
  maxAge: number | null;
  isActive: boolean;
  isOpen: boolean;
  imageUrl: string | null;
}

interface HomeGroupScheduleInput {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string | null;
  isActive: boolean;
}

// =============================================================================
// QUERY HOOK
// =============================================================================

interface UseHomeGroupResult {
  homeGroup: HomeGroup | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useHomeGroup(homeGroupId: string | undefined): UseHomeGroupResult {
  const [result, reexecuteQuery] = useQuery<{ homeGroup: HomeGroup | null }>({
    query: HomeGroupDocument,
    variables: { id: homeGroupId },
    pause: !homeGroupId,
  });

  return {
    homeGroup: result.data?.homeGroup ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

interface UseCreateHomeGroupResult {
  createHomeGroup: (input: HomeGroupInput) => Promise<string>;
  isLoading: boolean;
}

export function useCreateHomeGroup(): UseCreateHomeGroupResult {
  const [result, executeMutation] = useMutation<{ createHomeGroup: { id: string } }>(
    CreateHomeGroupDocument
  );

  const createHomeGroup = useCallback(
    async (input: HomeGroupInput): Promise<string> => {
      const response = await executeMutation({ input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка создания группы");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-home-groups", action: "createHomeGroup" },
        });
        throw err;
      }
      
      if (!response.data?.createHomeGroup?.id) {
        throw new Error("No data returned from createHomeGroup");
      }
      return response.data.createHomeGroup.id;
    },
    [executeMutation]
  );

  return { createHomeGroup, isLoading: result.fetching };
}

interface UseUpdateHomeGroupResult {
  updateHomeGroup: (id: string, input: HomeGroupInput) => Promise<void>;
  isLoading: boolean;
}

export function useUpdateHomeGroup(): UseUpdateHomeGroupResult {
  const [result, executeMutation] = useMutation<{ updateHomeGroup: { id: string } }>(
    UpdateHomeGroupDocument
  );

  const updateHomeGroup = useCallback(
    async (id: string, input: HomeGroupInput): Promise<void> => {
      const response = await executeMutation({ id, input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления группы");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-home-groups", action: "updateHomeGroup" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { updateHomeGroup, isLoading: result.fetching };
}

interface UseDeleteHomeGroupResult {
  deleteHomeGroup: (id: string) => Promise<void>;
  isLoading: boolean;
}

export function useDeleteHomeGroup(): UseDeleteHomeGroupResult {
  const [result, executeMutation] = useMutation<{ deleteHomeGroup: boolean }>(
    DeleteHomeGroupDocument
  );

  const deleteHomeGroup = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeMutation({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления группы");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-home-groups", action: "deleteHomeGroup" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { deleteHomeGroup, isLoading: result.fetching };
}

interface UseAddHomeGroupScheduleResult {
  addSchedule: (homeGroupId: string, input: HomeGroupScheduleInput) => Promise<string>;
  isLoading: boolean;
}

export function useAddHomeGroupSchedule(): UseAddHomeGroupScheduleResult {
  const [result, executeMutation] = useMutation<{
    addHomeGroupSchedule: { id: string };
  }>(AddScheduleDocument);

  const addSchedule = useCallback(
    async (homeGroupId: string, input: HomeGroupScheduleInput): Promise<string> => {
      const response = await executeMutation({ homeGroupId, input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка добавления расписания");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-home-groups", action: "addSchedule" },
        });
        throw err;
      }
      
      if (!response.data?.addHomeGroupSchedule?.id) {
        throw new Error("No data returned from addHomeGroupSchedule");
      }
      return response.data.addHomeGroupSchedule.id;
    },
    [executeMutation]
  );

  return { addSchedule, isLoading: result.fetching };
}

interface UseDeleteHomeGroupScheduleResult {
  deleteSchedule: (scheduleId: string) => Promise<void>;
  isLoading: boolean;
}

export function useDeleteHomeGroupSchedule(): UseDeleteHomeGroupScheduleResult {
  const [result, executeMutation] = useMutation<{ deleteHomeGroupSchedule: boolean }>(
    DeleteScheduleDocument
  );

  const deleteSchedule = useCallback(
    async (scheduleId: string): Promise<void> => {
      const response = await executeMutation({ scheduleId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления расписания");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-home-groups", action: "deleteSchedule" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { deleteSchedule, isLoading: result.fetching };
}
